import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";

const linkStyle = { color: "#2563EB" };

function groupBy(items, keyOf) {
  return items.reduce((acc, item) => {
    const key = keyOf(item);
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {});
}

export function EquipmentTickList({ areas, equipment, selected, setSelected }) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => equipment.filter(e => !q || [e.make, e.model, e.serial_number, e.type]
    .some(v => (v || "").toLowerCase().includes(q))), [equipment, q]);
  const byArea = groupBy(visible, e => e.area_id);
  const types = Object.keys(groupBy(visible, e => e.type || "Unassigned")).sort();
  const toggleMany = (ids, on) => setSelected(current => {
    const next = new Set(current);
    ids.forEach(id => (on ? next.add(id) : next.delete(id)));
    return [...next];
  });
  const allOn = ids => ids.length > 0 && ids.every(id => selected.includes(id));

  return (
    <div className="rounded-[12px] p-3" style={{ border: "1px solid #E7EAEE" }} data-testid="equipment-tick-list">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Input className="h-9 max-w-[240px] rounded-[10px]" placeholder="Search equipment" value={query}
               onChange={e => setQuery(e.target.value)} data-testid="equipment-tick-search" />
        <span className="text-[12.5px] font-medium" data-testid="equipment-tick-count">
          {selected.length} selected
        </span>
      </div>
      {types.length > 1 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[12px]">
          <span style={{ color: "#5B6472" }}>Select all by type:</span>
          {types.map(type => {
            const ids = visible.filter(e => (e.type || "Unassigned") === type).map(e => e.id);
            return (
              <button key={type} type="button" className="underline" style={linkStyle}
                      onClick={() => toggleMany(ids, !allOn(ids))} data-testid={`equipment-tick-type-${type}`}>
                {allOn(ids) ? "Clear" : "All"} {type}
              </button>
            );
          })}
        </div>
      )}
      {visible.length === 0 && (
        <div className="text-[12.5px] mt-3" style={{ color: "#8A93A1" }}>No equipment at this site.</div>
      )}
      <div className="mt-2 max-h-[320px] overflow-y-auto space-y-3">
        {areas.filter(a => byArea[a.id]).map(area => {
          const ids = byArea[area.id].map(e => e.id);
          return (
            <div key={area.id} data-testid={`equipment-tick-area-${area.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-condensed uppercase tracking-wider text-[11px] font-semibold" style={{ color: "#5B6472" }}>{area.name}</span>
                <button type="button" className="underline text-[12px]" style={linkStyle}
                        onClick={() => toggleMany(ids, !allOn(ids))} data-testid={`equipment-tick-area-all-${area.id}`}>
                  {allOn(ids) ? "Clear area" : "Select all"}
                </button>
              </div>
              {byArea[area.id].map(e => (
                <label key={e.id} className="flex items-center gap-2 py-1 text-[13px] cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-[#2563EB]" checked={selected.includes(e.id)}
                         onChange={ev => toggleMany([e.id], ev.target.checked)} data-testid={`equipment-tick-${e.id}`} />
                  <span>{e.type || "Unassigned"} · {[e.make, e.model].filter(Boolean).join(" ") || "—"}</span>
                  <span className="mono text-[11.5px]" style={{ color: "#5B6472" }}>{e.serial_number || "no serial"}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
